import React, { useState } from "react";
import { ClaimPage } from "./claimPage";
import { ClaimView } from "./claimView";
import { ClaimForm } from "./claimForm";
import { Constants } from "../../../assets/utils/lib/constants";
import { ClaimDto, SearchClaimDto } from "../../../services/dto";
import { ClaimService } from "./claimService";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { useQueryClient } from "@tanstack/react-query";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { isGridActionType } from "../../../assets/utils/helpers";
import { LoadingIndicator } from "../../../assets/vfx";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const ClaimContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [data, setData] = useState<ClaimDto>();
  const [drawerType, setDrawerType] = useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );

  const claimQuery = ClaimService.getClaimQuery(
    data?.id ?? Constants.emptyGuid
  );
  const claimTypesQuery = ClaimService.getClaimTypesQuery();

  const { mutateAsync: saveClaim } = ClaimService.save();
  const { mutate: deleteClaim } = ClaimService.deleteClaim();

  const isEditMode: boolean = !!(data?.id && data.id !== Constants.emptyGuid);
  const closeDrawer = (): void => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const refreshGrid = (): void => {
    isEditMode && claimQuery.refetch();
    queryClient.invalidateQueries({ queryKey: ["claimGridQuery"] });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: ClaimDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message: string = isEditMode ? "Claim updated" : "Claim saved";

    return await saveClaim(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleDelete = (): void => {
    deleteClaim(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => handleApiResponse(response, "Claim deleted"),
    });
  };

  const handleEdit = (): void => {
    setData(claimQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, claimQuery.data?.id);
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as ClaimDto);
      setSearchAction(actionType, dataId);
    }
  };

  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <ClaimForm
            show
            claimTypes={claimTypesQuery.data ?? []}
            onHide={closeDrawer}
            onSave={handleSubmit}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <ClaimForm
            show={!!claimQuery.data}
            loading={claimQuery.isLoading}
            data={claimQuery.data}
            claimTypes={claimTypesQuery.data ?? []}
            onHide={closeDrawer}
            onSave={handleSubmit}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <ClaimView
            show={!!claimQuery.data}
            data={claimQuery.data}
            claimTypes={claimTypesQuery.data ?? []}
            onHide={closeDrawer}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );

      default:
        return null;
    }
  };

  if (claimTypesQuery.isLoading) {
    return <LoadingIndicator isFullPage />;
  }

  return (
    <>
      <ClaimPage
        claimTypes={claimTypesQuery.data ?? []}
        onCustomEvent={handleCustomEvent}
      />
      {renderDrawerContent()}
    </>
  );
};
